import {AlchemyNft} from '@/utils/Web3Client';
import {REPLICANTX_CONTRACT_ADDRESS} from '@/utils/white-rabbit/contracts/replicant-x';
import {WR_KEY_CONTRACT_ADDRESS} from '@/utils/white-rabbit/contracts/wr-key-contracts';

enum DominionXSupportedNFTs {
  ReplicantX = 'ReplicantX',
  WhiteRabbitKeys = 'WhiteRabbitKey',
}

const dominionxNFTsContractAddress = (nft: DominionXSupportedNFTs) => {
  switch (nft) {
    case DominionXSupportedNFTs.ReplicantX:
      return REPLICANTX_CONTRACT_ADDRESS.toLowerCase();
    case DominionXSupportedNFTs.WhiteRabbitKeys:
      return WR_KEY_CONTRACT_ADDRESS.toLowerCase();
  }
};

export const isReplicantXNFT = (nft: AlchemyNft) => {
  return (
    nft.address?.toLowerCase() ===
    dominionxNFTsContractAddress(DominionXSupportedNFTs.ReplicantX)
  );
};

export const isReplicantXContract = (contractAddress: string) => {
  return (
    contractAddress.toLowerCase() ===
    dominionxNFTsContractAddress(DominionXSupportedNFTs.ReplicantX)
  );
};

export const isWhiteRabbitKeyNFT = (nft: AlchemyNft) => {
  return (
    nft.address?.toLowerCase() ===
    dominionxNFTsContractAddress(DominionXSupportedNFTs.WhiteRabbitKeys)
  );
};

export const isWhiteRabbitKeyContract = (contractAddress: string) => {
  return (
    contractAddress.toLowerCase() ===
    dominionxNFTsContractAddress(DominionXSupportedNFTs.WhiteRabbitKeys)
  );
};

export const getNFTQuantityText = (nfts: AlchemyNft[]) => {
  const supportedNFTs = getSupportedNFTsToTokenIds(nfts);
  const replicantXTokenIds = supportedNFTs[DominionXSupportedNFTs.ReplicantX];
  const whiteRabbitKeysTokenIds =
    supportedNFTs[DominionXSupportedNFTs.WhiteRabbitKeys];
  const hasReplicantXTokens = replicantXTokenIds.length > 0;
  const hasWhiteRabbitTokens = whiteRabbitKeysTokenIds.length > 0;
  const replicantXQuantity = hasReplicantXTokens
    ? `${replicantXTokenIds.length} ${
        replicantXTokenIds.length > 1 ? 'ReplicantX NFTs' : 'ReplicantX'
      }`
    : '';
  const whiteRabbitKeysQuantity = hasWhiteRabbitTokens
    ? `${whiteRabbitKeysTokenIds.length} ${
        whiteRabbitKeysTokenIds.length > 1
          ? 'White Rabbit Key NFTs'
          : 'White Rabbit Key'
      }`
    : '';
  return `${replicantXQuantity}${
    hasReplicantXTokens && hasWhiteRabbitTokens ? ' and ' : ''
  }${whiteRabbitKeysQuantity}`;
};

export const getUniqueContractAddressesFromNFTs = (nfts: AlchemyNft[]) => {
  const contractAddresses = nfts
    .filter((nft) => {
      return nft.address != undefined;
    })
    .map((nft) => nft.address!);
  return Array.from(new Set(contractAddresses));
};

export const formatContractAddressesAndTokenIdsParamsWithNFTs = (
  nfts: AlchemyNft[]
) => {
  const addresses: string[] = [];
  const tokenIds: string[][] = [];
  const addressToTokenIds = getContractAddressesToTokenIds(nfts);
  for (const address in addressToTokenIds) {
    addresses.push(address);
    tokenIds.push(addressToTokenIds[address]);
  }
  return {contractAddresses: addresses, tokenIds: tokenIds};
};

const getSupportedNFTsToTokenIds = (nfts: AlchemyNft[]) => {
  const nftsToTokenIds: Record<DominionXSupportedNFTs, string[]> = {
    ReplicantX: [],
    WhiteRabbitKey: [],
  };
  for (const nft of nfts) {
    if (nft.address == undefined) {
      continue;
    }
    if (isReplicantXContract(nft.address)) {
      nftsToTokenIds[DominionXSupportedNFTs.ReplicantX].push(nft.id.toString());
    } else if (isWhiteRabbitKeyContract(nft.address)) {
      nftsToTokenIds[DominionXSupportedNFTs.WhiteRabbitKeys].push(
        nft.id.toString()
      );
    } else {
      continue;
    }
  }
  return nftsToTokenIds;
};

const getContractAddressesToTokenIds = (nfts: AlchemyNft[]) => {
  return nfts.reduce((mappings: Record<string, string[]>, nft) => {
    if (!nft.address) {
      return mappings;
    }
    return {
      ...mappings,
      [nft.address]: (mappings[nft.address] || []).concat(nft.id.toString()),
    };
  }, {});
};
